import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { 
    LayoutDashboard, 
    Calendar, 
    Users, 
    Wallet, 
    Settings,
    LogOut,
    Menu,
    X,
    QrCode,
    CreditCard,
    FileText,
    ChevronLeft,
    ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { cn } from "@/lib/utils";

// TRIMY Shield Logo Component
const TrimyLogo = ({ collapsed }) => (
    <div className="flex items-center gap-3">
        <svg width="40" height="40" viewBox="0 0 48 48" fill="none" className="flex-shrink-0">
            <defs>
                <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#00D4FF" />
                    <stop offset="100%" stopColor="#12006d" />
                </linearGradient>
            </defs>
            <path 
                d="M24 4L6 10v12c0 11 8 18 18 22 10-4 18-11 18-22V10L24 4z" 
                fill="url(#shieldGrad)" 
            />
            <path 
                d="M24 12v6M24 22v2M18 16h12M16 22h4M28 22h4" 
                stroke="white" 
                strokeWidth="2" 
                strokeLinecap="round" 
            />
            <circle cx="24" cy="28" r="2" fill="white" />
            <path d="M20 32h8" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </svg>
        {!collapsed && (
            <div>
                <h1 className="font-heading font-bold text-xl text-white tracking-wide">TRIMY</h1>
                <p className="text-xs text-white/60">Business Manager</p>
            </div>
        )}
    </div>
);

const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/calendar", label: "Calendar", icon: Calendar },
    { path: "/clients", label: "Clients", icon: Users },
    { path: "/accounting", label: "Accounting", icon: Wallet },
    { path: "/invoices", label: "Invoices", icon: FileText },
    { path: "/qrcode", label: "QR Booking", icon: QrCode },
    { path: "/subscription", label: "Subscription", icon: CreditCard },
    { path: "/settings", label: "Settings", icon: Settings },
];

export default function Layout() {
    const { user, logout } = useAuth();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const location = useLocation();

    const handleLogout = () => {
        logout();
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Desktop Sidebar */}
            <aside 
                className={cn(
                    "hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300",
                    collapsed ? "lg:w-20" : "lg:w-64"
                )}
            >
                <div 
                    className="flex flex-col flex-grow px-4 py-6"
                    style={{ background: 'linear-gradient(180deg, #12006d 0%, #0d004d 100%)' }}
                >
                    {/* Logo & Collapse Button */}
                    <div className="flex items-center justify-between mb-8 px-2">
                        <TrimyLogo collapsed={collapsed} />
                        <button 
                            onClick={() => setCollapsed(!collapsed)}
                            className="w-7 h-7 flex items-center justify-center bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors"
                        >
                            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                        </button>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 space-y-1">
                        {navItems.map((item) => {
                            const isActive = location.pathname === item.path;
                            return (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    className={cn(
                                        "relative flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all",
                                        isActive 
                                            ? "bg-[#00D4FF]/15 text-[#00D4FF]" 
                                            : "text-white/70 hover:bg-white/10 hover:text-white"
                                    )}
                                    title={collapsed ? item.label : undefined}
                                >
                                    {isActive && (
                                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-[#00D4FF] rounded-r-full" />
                                    )}
                                    <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                                    {!collapsed && <span>{item.label}</span>}
                                </NavLink>
                            );
                        })}
                    </nav>

                    {/* User section */}
                    <div className="mt-auto pt-4 border-t border-white/10">
                        <div className={cn(
                            "flex items-center gap-3 px-2 mb-4",
                            collapsed && "justify-center"
                        )}>
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00D4FF] to-[#12006d] flex items-center justify-center flex-shrink-0">
                                <span className="text-sm font-semibold text-white">
                                    {user?.owner_name?.charAt(0) || 'U'}
                                </span>
                            </div>
                            {!collapsed && (
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-white truncate">{user?.owner_name}</p>
                                    <p className="text-xs text-white/60 truncate">{user?.business_name}</p>
                                </div>
                            )}
                        </div>
                        
                        <Button 
                            variant="ghost" 
                            className={cn(
                                "w-full text-white/70 hover:text-red-400 hover:bg-red-500/10",
                                collapsed ? "justify-center px-2" : "justify-start"
                            )}
                            onClick={handleLogout}
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-4 h-4" />
                            {!collapsed && <span className="ml-2">Logout</span>}
                        </Button>
                    </div>
                </div>
            </aside>

            {/* Mobile Header */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-gray-200">
                <div className="flex items-center justify-between px-4 h-16">
                    <div className="flex items-center gap-2">
                        <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
                            <defs>
                                <linearGradient id="mobileShieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#00D4FF" />
                                    <stop offset="100%" stopColor="#12006d" />
                                </linearGradient>
                            </defs>
                            <path 
                                d="M24 4L6 10v12c0 11 8 18 18 22 10-4 18-11 18-22V10L24 4z" 
                                fill="url(#mobileShieldGrad)" 
                            />
                            <path d="M24 12v6M24 22v2M18 16h12" stroke="white" strokeWidth="2" strokeLinecap="round" />
                            <circle cx="24" cy="28" r="2" fill="white" />
                        </svg>
                        <span className="font-heading font-bold text-[#12006d]">TRIMY</span>
                    </div>
                    <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        data-testid="mobile-menu-toggle"
                    >
                        {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </Button>
                </div>

                {/* Mobile Menu Dropdown */}
                {mobileMenuOpen && (
                    <div 
                        className="px-4 py-4 space-y-1 border-b border-gray-200"
                        style={{ background: 'linear-gradient(180deg, #12006d 0%, #0d004d 100%)' }}
                    >
                        {navItems.map((item) => {
                            const isActive = location.pathname === item.path;
                            return (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={cn(
                                        "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all",
                                        isActive 
                                            ? "bg-[#00D4FF]/15 text-[#00D4FF]" 
                                            : "text-white/70"
                                    )}
                                >
                                    <item.icon className="w-5 h-5" strokeWidth={1.5} />
                                    {item.label}
                                </NavLink>
                            );
                        })}
                        <Button 
                            variant="ghost" 
                            className="w-full justify-start text-white/70 mt-2 hover:text-red-400"
                            onClick={handleLogout}
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Logout
                        </Button>
                    </div>
                )}
            </header>

            {/* Mobile Bottom Navigation */}
            <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-gray-200">
                <div className="flex justify-around items-center h-16">
                    {navItems.slice(0, 5).map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                className={cn(
                                    "flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all",
                                    isActive 
                                        ? "text-[#00D4FF]" 
                                        : "text-gray-500"
                                )}
                            >
                                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                                <span className="text-xs font-medium">{item.label.split(' ')[0]}</span>
                            </NavLink>
                        );
                    })}
                </div>
            </nav>

            {/* Main Content */}
            <main className={cn(
                "transition-all duration-300",
                collapsed ? "lg:pl-20" : "lg:pl-64"
            )}>
                <div className="pt-16 pb-20 lg:pt-0 lg:pb-0 min-h-screen">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
